import type { TUnit } from '@/types/index.js';

type TMatrixObject = {
	condition: (n: number) => boolean;
	divider: number;
	unit: string;
};
const fallbackUnit: TMatrixObject = { condition: () => true, divider: Math.pow(10, -3), unit: 'km' };
const matrix: TMatrixObject[] = [
	{ condition: (n: number) => n < Math.pow(10, -2), divider: Math.pow(10, 3), unit: 'mm' },
	{ condition: (n: number) => n < 1, divider: Math.pow(10, 2), unit: 'cm' },
	{ condition: (n: number) => n < Math.pow(10, 3), divider: 1, unit: 'm' },
	fallbackUnit,
];

// Keeps a number and its unit on the same line
const NO_BREAK_SPACE = '\u00a0';

// A length in the unit that suits it, with thousands separated: "1,392,000 km", "1.74 m"
export const toProperUnit = (value: number): string => {
	const match = matrix.find((obj) => obj.condition(value)) ?? fallbackUnit;
	return `${roundToTwoDecimals(value * match.divider).toLocaleString('en-US')}${NO_BREAK_SPACE}${match.unit}`;
};

// An amount in millions of US dollars: whole millions from 100 up ("$2,924M"), two decimals below
const toDollarMillions = (value: number): string =>
	`$${(value >= 100 ? Math.round(value) : roundToTwoDecimals(value)).toLocaleString('en-US')}M`;

// A number of episodes: "191 episodes", "1 episode". A zoomed-in grid can ask for a fraction of one
const toEpisodes = (value: number): string => {
	const count = roundToTwoDecimals(value);
	return `${count.toLocaleString('en-US')}${NO_BREAK_SPACE}${count === 1 ? 'episode' : 'episodes'}`;
};

// Points of a score: "10 points", "1 point"
const toPoints = (value: number): string => {
	const count = roundToTwoDecimals(value);
	return `${count.toLocaleString('en-US')}${NO_BREAK_SPACE}${count === 1 ? 'point' : 'points'}`;
};

// A size in its dataset's unit: a length in the unit that suits it, an amount of money or a count
export const formatMeasure = (value: number, unit: TUnit = 'metre'): string => {
	if (unit === 'usd-million') return toDollarMillions(value);
	if (unit === 'episodes') return toEpisodes(value);
	if (unit === 'points') return toPoints(value);
	return toProperUnit(value);
};

// Large numbers of km in words, largest first
const LARGE_KM: [number, string][] = [
	[1e12, 'trillion'],
	[1e9, 'billion'],
	[1e6, 'million'],
];

/**
 * The size of one grid cell, as formatMeasure gives it, but lengths of a million km and more in
 * words, "100 million km" rather than "100,000,000 km": a grid zoomed out over black holes would
 * otherwise print a dozen zeros.
 */
export const formatGridSize = (value: number, unit: TUnit = 'metre'): string => {
	const km = value / 1000;
	const large = unit === 'metre' ? LARGE_KM.find(([size]) => km >= size) : undefined;
	if (!large) return formatMeasure(value, unit);
	const [size, word] = large;
	return `${roundToTwoDecimals(km / size).toLocaleString('en-US')}${NO_BREAK_SPACE}${word}${NO_BREAK_SPACE}km`;
};

// Every character a label's measure can use, in any unit, for the label fonts
export const MEASURE_GLYPHS = '0123456789,.× kmc$Mepisodnt';

// Rounding (not flooring) absorbs float noise: 0.29 * 100 is 28.999999999999996
export const roundToTwoDecimals = (num: number): number => Math.round(num * 100) / 100;
